// Firebase Client - Production mode uses environment variables
// For demo mode, use firebase-mock.js
import { 
  getFirebaseAuth, 
  getFirebaseDb, 
  getFirebaseFunctions, 
  firebaseReady, 
  firebaseError,
  showFirebaseConfigError,
  onAuthStateChanged, 
  signOut,
  doc,
  setDoc,
  serverTimestamp,
  getDoc
} from './firebaseClient.js';
import { router } from './router.js';
import { store } from './store.js';
import { renderLogin, renderSignup } from './ui/auth.js';
import { renderDashboard } from './ui/dashboard.js';
import { renderDealWizard } from './ui/dealWizard.js';
import { renderDealDetail } from './ui/dealDetail.js';
import { renderSettings } from './ui/settings.js';
import { renderNotifications } from './ui/notifications.js';
import { renderDealsList } from './ui/dealsList.js';
import { renderAccount } from './ui/account.js';
import { renderMarketplace, renderListingDetail } from './ui/marketplace.js';
import { renderMarketplaceNew } from './ui/marketplaceNew.js';
import { renderTemplates } from './ui/templates.js';
import { renderPeople } from './ui/people.js';
import { renderTerms } from './ui/terms.js';
import { Navbar, showToast } from './ui/components.js';
import { acceptInvite } from './api.js';

// Admin Modules
import { renderAdminOverview } from './ui/admin/overview.js';
import { renderAdminUsers } from './ui/admin/users.js';
import { renderAdminAgreements } from './ui/admin/agreements.js';
import { renderAdminMarketplace } from './ui/admin/marketplace.js';
import { renderAdminPayments } from './ui/admin/payments.js';
import { renderAdminSupport } from './ui/admin/support.js';
import { renderAdminCases } from './ui/admin/cases.js';
import { renderAdminModeration } from './ui/admin/moderation.js';
import { renderAdminNotifications } from './ui/admin/notifications.js';
import { renderAdminTemplates } from './ui/admin/templates.js';
import { renderAdminAudit } from './ui/admin/audit.js';

// Show blocking error UI if Firebase env vars are missing
if (!firebaseReady) {
  showFirebaseConfigError();
  throw new Error(firebaseError || 'Firebase not configured');
}

// Get Firebase instances (will throw if not ready)
const auth = getFirebaseAuth();
const db = getFirebaseDb();
const functions = getFirebaseFunctions();

// Make Firebase instances and state globally available
window.auth = auth;
window.db = db;
window.firebaseFunctions = functions;
window.store = store;
window.firebaseReady = firebaseReady;
window.firebaseError = firebaseError;

// Global admin status cache
window.isAdminUser = false;

// Initialize auth state
onAuthStateChanged(auth, async (user) => {
    if (user) {
      store.setState({ user });
      
      // Check admin claim from token
      try {
        const tokenResult = await user.getIdTokenResult(true);
        window.isAdminUser = tokenResult.claims.admin === true;
      } catch (e) {
        window.isAdminUser = false;
      }
      
      // Create/update user document and check account status
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
          await setDoc(userRef, {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || '',
            theme: store.state.theme,
            emailNotifications: true,
            pushNotifications: true,
            status: 'active',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } else {
          // Check account status
          const userData = userDoc.data();
          if (userData.status === 'suspended') {
            showToast('Your account has been suspended. Contact support for assistance.', 'error');
            await signOut(auth);
            renderAccountSuspended();
            return;
          } else if (userData.status === 'paused') {
            showToast('Your account is paused. Some features may be limited.', 'warning');
            store.setState({ accountPaused: true });
          } else if (userData.status === 'deleted') {
            showToast('This account has been deactivated.', 'error');
            await signOut(auth);
            router.navigate('/login');
            return;
          }
        }
      } catch (error) {
        console.error('Error creating user document:', error);
      }
    } else {
      store.setState({ user: null, accountPaused: false });
    }
    
    // Trigger initial route
    router.handleRoute();
  });

// Register routes
router.register('/', renderLanding, false);
router.register('/login', renderLogin, false);
router.register('/signup', renderSignup, false);
router.register('/app', renderDashboard, true);
router.register('/deals', renderDealsList, true);
router.register('/deal/new', renderDealWizard, true);
router.register('/deal/:id', renderDealDetail, true);
router.register('/join/:token', renderJoinDeal, false);
router.register('/marketplace', renderMarketplaceGated, false);
router.register('/marketplace/new', renderMarketplaceNew, true);
router.register('/marketplace/:id', renderListingDetailGated, false);
router.register('/notifications', renderNotifications, true);
router.register('/settings', renderSettings, true);
router.register('/account', renderAccount, true);
router.register('/templates', renderTemplates, false);
router.register('/people', renderPeople, true);
router.register('/terms', renderTerms, false);

// Admin Routes - all require auth (admin check is done in each module)
router.register('/admin', renderAdminOverview, true);
router.register('/admin/overview', renderAdminOverview, true);
router.register('/admin/users', renderAdminUsers, true);
router.register('/admin/agreements', renderAdminAgreements, true);
router.register('/admin/marketplace', renderAdminMarketplace, true);
router.register('/admin/payments', renderAdminPayments, true);
router.register('/admin/support', renderAdminSupport, true);
router.register('/admin/cases', renderAdminCases, true);
router.register('/admin/moderation', renderAdminModeration, true);
router.register('/admin/notifications', renderAdminNotifications, true);
router.register('/admin/templates', renderAdminTemplates, true);
router.register('/admin/audit', renderAdminAudit, true);

// Stripe Connect Routes
router.register('/connect/return', renderConnectReturn, true);
router.register('/connect/refresh', renderConnectRefresh, true);

// Admin Bootstrap Route
router.register('/admin-setup', renderAdminSetup, false); // accessible without auth

// Admin Setup Page - for bootstrapping first admin
async function renderAdminSetup() {
  const { user } = store.getState();
  const content = document.getElementById('content');
  
  content.innerHTML = `
    <div class="min-h-screen bg-gradient-to-br from-purple-50 to-navy-50 dark:from-navy-900 dark:to-navy-800 flex items-center justify-center p-4">
      <div class="max-w-md w-full bg-white dark:bg-navy-800 rounded-2xl shadow-2xl p-8">
        <div class="text-center mb-6">
          <div class="text-5xl mb-4">🔐</div>
          <h1 class="text-2xl font-bold text-navy-900 dark:text-white">Admin Setup</h1>
          <p class="text-navy-600 dark:text-navy-400 mt-2">
            Bootstrap admin access for your account
          </p>
        </div>
        
        <div id="setup-status" class="mb-6">
          ${user ? `
            <div class="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
              <p class="text-emerald-800 dark:text-emerald-200 text-sm">
                <strong>Signed in as:</strong> ${user.email}
              </p>
            </div>
          ` : `
            <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p class="text-amber-800 dark:text-amber-200 text-sm">
                Please <a href="#/auth" class="underline font-semibold">sign in</a> first to set up admin access.
              </p>
            </div>
          `}
        </div>
        
        <div id="admin-check" class="mb-6 hidden">
          <div class="flex items-center justify-center space-x-2">
            <div class="spinner w-5 h-5"></div>
            <span class="text-navy-600 dark:text-navy-400">Checking admin status...</span>
          </div>
        </div>
        
        <div id="already-admin" class="hidden mb-6">
          <div class="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 text-center">
            <div class="text-3xl mb-2">✅</div>
            <p class="text-purple-800 dark:text-purple-200 font-semibold">You're already an admin!</p>
            <a href="#/admin" class="inline-block mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
              Go to Admin Portal
            </a>
          </div>
        </div>
        
        <div id="bootstrap-form" class="${user ? '' : 'hidden'}">
          <button 
            onclick="bootstrapAdmin()"
            id="bootstrap-btn"
            class="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold disabled:opacity-50"
          >
            🚀 Make Me Admin
          </button>
          <p class="text-xs text-navy-500 dark:text-navy-400 mt-3 text-center">
            This only works if no admins exist yet, or your email is pre-approved.
          </p>
        </div>
        
        <div id="bootstrap-result" class="hidden"></div>
        
        <div class="mt-8 pt-6 border-t border-navy-200 dark:border-navy-700 text-center">
          <a href="#/" class="text-navy-500 hover:text-navy-700 dark:hover:text-navy-300 text-sm">
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  `;
  
  // Check if user is already admin
  if (user && window.isAdminUser) {
    document.getElementById('bootstrap-form').classList.add('hidden');
    document.getElementById('already-admin').classList.remove('hidden');
  }
}

// Global handler for admin bootstrap
window.bootstrapAdmin = async () => {
  const btn = document.getElementById('bootstrap-btn');
  const result = document.getElementById('bootstrap-result');
  
  btn.textContent = 'Setting up...';
  btn.disabled = true;
  
  try {
    const { adminBootstrap } = await import('./api.js');
    const response = await adminBootstrap();
    
    result.classList.remove('hidden');
    result.innerHTML = `
      <div class="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 text-center">
        <div class="text-3xl mb-2">🎉</div>
        <p class="text-emerald-800 dark:text-emerald-200 font-semibold">${response.message}</p>
        <p class="text-emerald-700 dark:text-emerald-300 text-sm mt-2">
          ${response.firstAdmin ? 'You are the first admin!' : ''}
        </p>
        <button 
          onclick="location.reload()"
          class="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
        >
          Sign Out & Sign Back In
        </button>
      </div>
    `;
    document.getElementById('bootstrap-form').classList.add('hidden');
    
    // Sign out to refresh the token - use the already-imported signOut and auth
    await signOut(auth);
    
  } catch (error) {
    console.error('Bootstrap error:', error);
    result.classList.remove('hidden');
    result.innerHTML = `
      <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p class="text-red-800 dark:text-red-200 font-semibold">Setup Failed</p>
        <p class="text-red-700 dark:text-red-300 text-sm mt-1">${error.message || 'Unknown error'}</p>
      </div>
    `;
    btn.textContent = '🚀 Try Again';
    btn.disabled = false;
  }
};

// Stripe Connect Return Page - shown after completing onboarding
async function renderConnectReturn() {
  const { user } = store.getState();
  const content = document.getElementById('content');
  
  content.innerHTML = `
    ${Navbar({ user, isAdmin: window.isAdminUser })}
    <div class="min-h-screen bg-gradient-to-br from-emerald-50 to-navy-50 dark:from-navy-900 dark:to-navy-800 flex items-center justify-center p-4">
      <div class="max-w-md w-full bg-white dark:bg-navy-800 rounded-2xl shadow-2xl p-8 text-center">
        <div class="text-6xl mb-4 animate-bounce-slow" id="connect-icon">⏳</div>
        <h1 class="text-2xl font-bold text-navy-900 dark:text-white mb-4" id="connect-title">Verifying Connection...</h1>
        <p class="text-navy-600 dark:text-navy-400 mb-6" id="connect-message">
          Please wait while we verify your Stripe account status.
        </p>
        <div id="connect-actions" class="hidden space-y-3">
          <a href="#/settings" class="block w-full px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold">
            Go to Settings
          </a>
          <a href="#/app" class="block text-navy-500 hover:text-navy-700 text-sm">
            Back to Dashboard
          </a>
        </div>
        <div id="connect-loading" class="flex justify-center">
          <div class="spinner w-8 h-8"></div>
        </div>
      </div>
    </div>
  `;
  
  // Refresh connect status from Stripe
  try {
    const { refreshConnectStatus } = await import('./api.js');
    const result = await refreshConnectStatus();
    
    const icon = document.getElementById('connect-icon');
    const title = document.getElementById('connect-title');
    const message = document.getElementById('connect-message');
    const actions = document.getElementById('connect-actions');
    const loading = document.getElementById('connect-loading');
    
    loading.classList.add('hidden');
    actions.classList.remove('hidden');
    
    if (result.status?.payoutsEnabled && result.status?.chargesEnabled) {
      icon.textContent = '✅';
      title.textContent = 'Successfully Connected!';
      message.textContent = 'Your Stripe account is fully set up. You can now receive payouts.';
    } else if (result.status?.detailsSubmitted) {
      icon.textContent = '⏳';
      title.textContent = 'Almost Done!';
      message.textContent = 'Your details have been submitted. Stripe is reviewing your account. This usually takes a few minutes.';
    } else {
      icon.textContent = '⚠️';
      title.textContent = 'Setup Incomplete';
      message.innerHTML = `Your Stripe account setup is not complete. Please <a href="#/settings" class="text-emerald-600 underline">finish setup</a> to receive payouts.`;
    }
  } catch (error) {
    console.error('Error refreshing connect status:', error);
    document.getElementById('connect-icon').textContent = '❌';
    document.getElementById('connect-title').textContent = 'Error Verifying';
    document.getElementById('connect-message').textContent = error.message || 'Could not verify your Stripe connection. Please try again.';
    document.getElementById('connect-loading').classList.add('hidden');
    document.getElementById('connect-actions').classList.remove('hidden');
  }
}

// Stripe Connect Refresh Page - shown when link expired or user left early
async function renderConnectRefresh() {
  const { user } = store.getState();
  const content = document.getElementById('content');
  
  content.innerHTML = `
    ${Navbar({ user, isAdmin: window.isAdminUser })}
    <div class="min-h-screen bg-gradient-to-br from-gold-50 to-navy-50 dark:from-navy-900 dark:to-navy-800 flex items-center justify-center p-4">
      <div class="max-w-md w-full bg-white dark:bg-navy-800 rounded-2xl shadow-2xl p-8 text-center">
        <div class="text-6xl mb-4">🔄</div>
        <h1 class="text-2xl font-bold text-navy-900 dark:text-white mb-4">Session Expired</h1>
        <p class="text-navy-600 dark:text-navy-400 mb-6">
          Your Stripe onboarding session has expired or was interrupted. 
          No worries - click below to continue where you left off.
        </p>
        <button 
          onclick="restartStripeConnect()"
          id="restart-btn"
          class="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold mb-4"
        >
          Continue Setup
        </button>
        <a href="#/settings" class="block text-navy-500 hover:text-navy-700 text-sm">
          Back to Settings
        </a>
      </div>
    </div>
  `;
}

// Global handler for restarting Stripe Connect onboarding
window.restartStripeConnect = async () => {
  const btn = document.getElementById('restart-btn');
  if (btn) {
    btn.textContent = 'Redirecting...';
    btn.disabled = true;
  }
  
  try {
    const { setupStripeConnect } = await import('./api.js');
    const result = await setupStripeConnect();
    window.location.href = result.url;
  } catch (error) {
    console.error('Error restarting Stripe Connect:', error);
    showToast(error.message || 'Failed to restart setup. Please try again.', 'error');
    if (btn) {
      btn.textContent = 'Continue Setup';
      btn.disabled = false;
    }
  }
};

// Account suspended page
function renderAccountSuspended() {
  const content = document.getElementById('content');
  content.innerHTML = `
    <div class="min-h-screen bg-gradient-to-br from-navy-900 to-navy-800 flex items-center justify-center p-4">
      <div class="max-w-md w-full bg-white dark:bg-navy-800 rounded-2xl shadow-2xl p-8 text-center">
        <div class="text-6xl mb-6">🚫</div>
        <h1 class="text-2xl font-bold text-navy-900 dark:text-white mb-4">Account Suspended</h1>
        <p class="text-navy-600 dark:text-navy-400 mb-6">
          Your account has been suspended due to a violation of our terms of service or 
          pending investigation. If you believe this is an error, please contact our support team.
        </p>
        <button 
          onclick="showGetHelpModal()"
          class="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold mb-4"
        >
          Contact Support
        </button>
        <a 
          href="#/login" 
          class="text-navy-500 hover:text-navy-700 text-sm"
        >
          Back to Login
        </a>
      </div>
    </div>
    ${GetHelpModal()}
  `;
}

// Get Help Modal Component
function GetHelpModal() {
  return `
    <div id="get-help-modal" class="fixed inset-0 bg-black/50 hidden z-50 flex items-center justify-center">
      <div class="bg-white dark:bg-navy-800 rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold text-navy-900 dark:text-white">Get Help</h3>
          <button onclick="hideGetHelpModal()" class="text-navy-500 hover:text-navy-700">✕</button>
        </div>
        <form id="help-form" onsubmit="submitHelpRequest(event)" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-navy-700 dark:text-navy-300 mb-1">Category</label>
            <select id="help-category" required class="w-full px-3 py-2 rounded-lg border border-navy-200 dark:border-navy-600 bg-white dark:bg-navy-700">
              <option value="">Select a category...</option>
              <option value="account">Account Issue</option>
              <option value="payment">Payment Problem</option>
              <option value="deal">Deal Question</option>
              <option value="marketplace">Marketplace Issue</option>
              <option value="technical">Technical Problem</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-navy-700 dark:text-navy-300 mb-1">Subject</label>
            <input 
              type="text" 
              id="help-subject"
              required
              placeholder="Brief description of your issue"
              class="w-full px-3 py-2 rounded-lg border border-navy-200 dark:border-navy-600 bg-white dark:bg-navy-700"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-navy-700 dark:text-navy-300 mb-1">Description</label>
            <textarea 
              id="help-description"
              required
              rows="4"
              placeholder="Please describe your issue in detail..."
              class="w-full px-3 py-2 rounded-lg border border-navy-200 dark:border-navy-600 bg-white dark:bg-navy-700"
            ></textarea>
          </div>
          <div>
            <label class="block text-sm font-medium text-navy-700 dark:text-navy-300 mb-1">Related Deal ID (optional)</label>
            <input 
              type="text" 
              id="help-deal-id"
              placeholder="If this is about a specific deal"
              class="w-full px-3 py-2 rounded-lg border border-navy-200 dark:border-navy-600 bg-white dark:bg-navy-700"
            />
          </div>
          <button 
            type="submit"
            class="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold"
          >
            Submit Request
          </button>
        </form>
      </div>
    </div>
  `;
}

// Global help modal functions
window.showGetHelpModal = () => {
  document.getElementById('get-help-modal')?.classList.remove('hidden');
};

window.hideGetHelpModal = () => {
  document.getElementById('get-help-modal')?.classList.add('hidden');
  document.getElementById('help-form')?.reset();
};

window.submitHelpRequest = async (event) => {
  event.preventDefault();
  
  const category = document.getElementById('help-category')?.value;
  const subject = document.getElementById('help-subject')?.value;
  const description = document.getElementById('help-description')?.value;
  const dealId = document.getElementById('help-deal-id')?.value || null;
  
  if (!category || !subject || !description) {
    showToast('Please fill out all required fields', 'error');
    return;
  }
  
  try {
    const { user } = store.getState();
    const { addDoc, collection, serverTimestamp: ts } = await import('./firebaseClient.js');
    
    await addDoc(collection(db, 'supportTickets'), {
      userUid: user?.uid || null,
      userEmail: user?.email || 'anonymous',
      category,
      subject,
      description,
      dealId,
      status: 'open',
      priority: 'medium',
      createdAt: ts(),
      updatedAt: ts(),
    });
    
    showToast('Support request submitted! We\'ll get back to you soon.', 'success');
    hideGetHelpModal();
  } catch (error) {
    console.error('Error submitting help request:', error);
    showToast('Failed to submit request. Please try again.', 'error');
  }
};

// Marketplace auth gating wrapper
function renderMarketplaceGated(params) {
  const { user } = store.getState();
  if (!user) {
    sessionStorage.setItem('returnTo', '/marketplace');
    showToast('Please log in to access the marketplace', 'info');
    router.navigate('/login');
    return;
  }
  return renderMarketplace(params);
}

function renderListingDetailGated(params) {
  const { user } = store.getState();
  if (!user) {
    sessionStorage.setItem('returnTo', `/marketplace/${params.id}`);
    showToast('Please log in to view listing details', 'info');
    router.navigate('/login');
    return;
  }
  return renderListingDetail(params);
}

// Landing page
function renderLanding() {
  const { user } = store.getState();
  
  if (user) {
    router.navigate('/app');
    return;
  }
  
  const content = document.getElementById('content');
  content.innerHTML = `
    ${Navbar({ user: null })}

    <div class="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-navy-50 dark:from-navy-900 dark:via-navy-800 dark:to-navy-900 relative overflow-hidden">
      <!-- Animated Background Blobs -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div class="blob blob-1"></div>
        <div class="blob blob-2"></div>
        <div class="blob blob-3"></div>
      </div>
      
      <!-- Hero Section -->
      <section class="relative container mx-auto px-4 pt-20 pb-16">
        <div class="max-w-4xl mx-auto text-center">
          <div class="text-7xl mb-6 animate-bounce-slow">💰</div>
          <h1 class="text-5xl md:text-7xl font-bold mb-6">
            <span class="gradient-text">MoneyGood</span>
          </h1>
          <p class="text-xl md:text-2xl text-navy-700 dark:text-navy-300 mb-8 max-w-2xl mx-auto">
            A tool for creating and tracking agreements between people
          </p>
          <div class="flex gap-4 justify-center flex-wrap">
            <a 
              href="#/signup" 
              class="btn-primary bg-emerald-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-700 hover:scale-105 transition-all shadow-glow"
            >
              Create Agreement
            </a>
            <a 
              href="#/login" 
              onclick="sessionStorage.setItem('returnTo', '/marketplace')"
              class="glass-card px-8 py-4 rounded-xl font-bold text-lg text-navy-700 dark:text-white hover:scale-105 transition-all border border-navy-200 dark:border-navy-600"
            >
              Explore Marketplace
            </a>
          </div>
          <div class="mt-6 flex gap-4 justify-center flex-wrap text-sm">
            <a href="#/login" class="text-emerald-600 hover:text-emerald-700 font-semibold">Login</a>
            <span class="text-navy-400">•</span>
            <a href="#/templates" class="text-emerald-600 hover:text-emerald-700 font-semibold">Browse Templates</a>
          </div>
        </div>
      </section>
      
      <!-- Value Props - Glass Cards -->
      <section class="relative container mx-auto px-4 py-16">
        <div class="max-w-6xl mx-auto">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="glass-card p-8 rounded-2xl text-center group hover:scale-105 transition-all duration-300">
              <div class="text-5xl mb-4 group-hover:scale-110 transition-transform">💵</div>
              <h3 class="text-xl font-bold text-navy-900 dark:text-white mb-3">Money Agreements</h3>
              <p class="text-navy-600 dark:text-navy-400 text-sm">
                Conditional payments with clear terms and mutual confirmation
              </p>
            </div>
            
            <div class="glass-card p-8 rounded-2xl text-center group hover:scale-105 transition-all duration-300">
              <div class="text-5xl mb-4 group-hover:scale-110 transition-transform">📦</div>
              <h3 class="text-xl font-bold text-navy-900 dark:text-white mb-3">Goods & Services</h3>
              <p class="text-navy-600 dark:text-navy-400 text-sm">
                Exchange items or services with structured accountability
              </p>
            </div>
            
            <div class="glass-card p-8 rounded-2xl text-center group hover:scale-105 transition-all duration-300">
              <div class="text-5xl mb-4 group-hover:scale-110 transition-transform">🤝</div>
              <h3 class="text-xl font-bold text-navy-900 dark:text-white mb-3">Mutual Trust</h3>
              <p class="text-navy-600 dark:text-navy-400 text-sm">
                Both parties confirm outcomes with full transparency
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <!-- How It Works - Compact -->
      <section class="relative container mx-auto px-4 py-16">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-3xl md:text-4xl font-bold text-center text-navy-900 dark:text-white mb-12">
            How It Works
          </h2>
          
          <div class="grid grid-cols-1 md:grid-cols-5 gap-4 text-center">
            <div class="step-item">
              <div class="w-12 h-12 mx-auto bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg mb-3">1</div>
              <p class="font-semibold text-navy-900 dark:text-white">Create</p>
              <p class="text-xs text-navy-600 dark:text-navy-400">Set terms</p>
            </div>
            <div class="hidden md:flex items-center justify-center text-navy-300">→</div>
            <div class="step-item">
              <div class="w-12 h-12 mx-auto bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg mb-3">2</div>
              <p class="font-semibold text-navy-900 dark:text-white">Invite</p>
              <p class="text-xs text-navy-600 dark:text-navy-400">Share link</p>
            </div>
            <div class="hidden md:flex items-center justify-center text-navy-300">→</div>
            <div class="step-item">
              <div class="w-12 h-12 mx-auto bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg mb-3">3</div>
              <p class="font-semibold text-navy-900 dark:text-white">Fund</p>
              <p class="text-xs text-navy-600 dark:text-navy-400">If needed</p>
            </div>
            <div class="hidden md:flex items-center justify-center text-navy-300">→</div>
            <div class="step-item">
              <div class="w-12 h-12 mx-auto bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg mb-3">4</div>
              <p class="font-semibold text-navy-900 dark:text-white">Confirm</p>
              <p class="text-xs text-navy-600 dark:text-navy-400">Both agree</p>
            </div>
            <div class="hidden md:flex items-center justify-center text-navy-300">→</div>
            <div class="step-item">
              <div class="w-12 h-12 mx-auto bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg mb-3">5</div>
              <p class="font-semibold text-navy-900 dark:text-white">Complete</p>
              <p class="text-xs text-navy-600 dark:text-navy-400">Done!</p>
            </div>
          </div>
        </div>
      </section>
      
      <!-- Features Grid -->
      <section class="relative container mx-auto px-4 py-16">
        <div class="max-w-4xl mx-auto">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div class="p-4">
              <div class="text-3xl mb-2">📋</div>
              <p class="font-semibold text-navy-900 dark:text-white text-sm">Audit Log</p>
            </div>
            <div class="p-4">
              <div class="text-3xl mb-2">✅</div>
              <p class="font-semibold text-navy-900 dark:text-white text-sm">Mutual Confirm</p>
            </div>
            <div class="p-4">
              <div class="text-3xl mb-2">❄️</div>
              <p class="font-semibold text-navy-900 dark:text-white text-sm">Dispute Freeze</p>
            </div>
            <div class="p-4">
              <div class="text-3xl mb-2">💳</div>
              <p class="font-semibold text-navy-900 dark:text-white text-sm">Stripe Payments</p>
            </div>
          </div>
        </div>
      </section>
      
      <!-- CTA Section -->
      <section class="relative container mx-auto px-4 py-16">
        <div class="max-w-4xl mx-auto text-center glass-card rounded-3xl p-12 border border-emerald-200 dark:border-emerald-800">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-navy-900 dark:text-white">
            Ready to Get Started?
          </h2>
          <p class="text-lg mb-8 text-navy-600 dark:text-navy-400">
            Create your first agreement in minutes
          </p>
          <div class="flex gap-4 justify-center flex-wrap">
            <a 
              href="#/signup" 
              class="btn-primary bg-emerald-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-700 hover:scale-105 transition-all shadow-glow"
            >
              Get Started Free
            </a>
            <a 
              href="#/templates" 
              class="px-8 py-4 rounded-xl font-bold text-lg text-navy-700 dark:text-white border-2 border-navy-200 dark:border-navy-600 hover:border-emerald-500 hover:scale-105 transition-all"
            >
              Browse Templates
            </a>
          </div>
        </div>
      </section>
      
      <!-- Footer -->
      <footer class="relative container mx-auto px-4 py-8 text-center text-navy-600 dark:text-navy-400">
        <div class="flex justify-center gap-6 mb-4 text-sm">
          <a href="#/templates" class="hover:text-emerald-600 transition">Templates</a>
          <a href="#/terms" class="hover:text-emerald-600 transition">Terms & Policy</a>
        </div>
        <p class="text-sm">&copy; ${new Date().getFullYear()} MoneyGood. All rights reserved.</p>
        <p class="text-xs mt-2 text-navy-500">Use responsibly. See <a href="#/terms" class="underline hover:text-emerald-600">Terms</a>.</p>
      </footer>
    </div>
  `;
}

// Join deal page
async function renderJoinDeal(params) {
  const { token } = params;
  const { user } = store.getState();
  
  if (!user) {
    // Save intended destination
    sessionStorage.setItem('joinToken', token);
    showToast('Please login to join this agreement', 'warning');
    router.navigate('/login');
    return;
  }
  
  const content = document.getElementById('content');
  content.innerHTML = `
    ${Navbar({ user })}
    <div class="min-h-screen bg-gradient-to-br from-emerald-50 to-navy-50 dark:from-navy-900 dark:to-navy-800 flex items-center justify-center p-4">
      <div class="bg-white dark:bg-navy-800 rounded-2xl shadow-2xl p-8 w-full max-w-2xl">
        <div class="text-center">
          <div class="text-6xl mb-4 animate-bounce-slow">🤝</div>
          <h1 class="text-3xl font-bold text-navy-900 dark:text-white mb-2">Join Agreement</h1>
          <p class="text-navy-600 dark:text-navy-400 mb-6">Reviewing invitation details...</p>
          <div class="flex justify-center">
            ${Spinner({ size: 'lg' })}
          </div>
        </div>
        
        <div id="join-preview" class="mt-6"></div>
      </div>
    </div>
  `;
  
  try {
    // TODO: In real implementation, fetch deal preview by token first
    // For now, just accept the invite
    const result = await acceptInvite(token);
    
    // Show success state
    document.getElementById('join-preview').innerHTML = `
      <div class="p-6 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 text-center">
        <div class="text-4xl mb-3">✅</div>
        <p class="font-bold text-emerald-900 dark:text-emerald-300 mb-2">Successfully Joined!</p>
        <p class="text-sm text-emerald-800 dark:text-emerald-400">Redirecting to agreement details...</p>
      </div>
    `;
    
    showToast('Successfully joined the agreement!', 'success');
    setTimeout(() => {
      router.navigate(`/deal/${result.dealId}`);
    }, 1500);
  } catch (error) {
    console.error('Error joining agreement:', error);
    
    // Determine error reason
    let reason = 'invalid_token';
    let message = 'This invite link is invalid or has already been used.';
    
    if (error.message?.includes('expired')) {
      reason = 'expired';
      message = 'This invite link has expired. Please ask the sender for a new invitation.';
    } else if (error.message?.includes('already')) {
      message = 'This invitation has already been accepted.';
    } else if (error.message?.includes('own')) {
      message = 'You cannot join your own agreement.';
    }
    
    document.getElementById('join-preview').innerHTML = `
      <div class="p-6 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-center">
        <div class="text-4xl mb-3">🔗</div>
        <p class="font-bold text-red-900 dark:text-red-300 mb-2">Unable to Join Agreement</p>
        <p class="text-sm text-red-800 dark:text-red-400 mb-4">${message}</p>
        <div class="space-y-2">
          <button 
            onclick="window.location.hash = '/dashboard'" 
            class="block w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Go to Dashboard
          </button>
          <button 
            onclick="window.location.hash = '/deal/new'" 
            class="block w-full px-4 py-2 border border-navy-300 dark:border-navy-600 text-navy-700 dark:text-navy-300 rounded-lg hover:bg-navy-50 dark:hover:bg-navy-800"
          >
            Create New Agreement
          </button>
        </div>
      </div>
    `;
    
    showToast(message, 'error');
  }
}

// Notifications page handled by ui/notifications.js

// Global logout handler
window.handleLogout = async () => {
  try {
    await signOut(auth);
    showToast('Logged out successfully', 'success');
    router.navigate('/');
  } catch (error) {
    console.error('Logout error:', error);
    showToast('Failed to logout', 'error');
  }
};

// Floating Get Help Button - inject once on load
function injectFloatingHelpButton() {
  // Don't add if already exists
  if (document.getElementById('floating-help-btn')) return;
  
  const container = document.createElement('div');
  container.id = 'floating-help-container';
  container.innerHTML = `
    <button 
      id="floating-help-btn"
      onclick="showGetHelpModal()"
      class="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-full shadow-lg hover:bg-emerald-700 transition-all hover:scale-105"
      title="Get Help"
    >
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <span class="hidden sm:inline">Get Help</span>
    </button>
    ${GetHelpModal()}
  `;
  document.body.appendChild(container);
}

// Inject the help button when document is ready
if (document.readyState === 'complete') {
  injectFloatingHelpButton();
} else {
  window.addEventListener('load', injectFloatingHelpButton);
}

// Initialize app
console.log('MoneyGood app initialized');
